import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  LayoutDashboard,
  Menu,
  QrCode,
  Smartphone,
  Store,
  Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import seoPages from "@/data/seoPages.json";

const ICONS = { QrCode, Smartphone, Store, Wallet };

const PAGE_CONTENT = seoPages;

const CROSS_LINKS = [
  { to: "/qr-menu", label: "QR Menü", desc: "QR kod ile dijital içeriğinizi paylaşın." },
  { to: "/dijital-menu", label: "Dijital Menü", desc: "Güncellenebilir ürün ve hizmet sunumu." },
  { to: "/dijital-vitrin", label: "Dijital Vitrin", desc: "İşletmenizi tek bağlantıda sergileyin." },
  { to: "/qr-menu-fiyatlari", label: "QR Menü Fiyatları", desc: "Abonelik seçeneklerini karşılaştırın." },
];

export default function SeoLandingPage({ pageKey }) {
  const navigate = useNavigate();
  const params = useParams();
  const resolvedKey = pageKey || params.pageKey;
  const page = PAGE_CONTENT[resolvedKey];

  if (!page) return null;

  const Icon = ICONS[page.icon] || LayoutDashboard;

  return (
    <div className="min-h-screen bg-white text-zinc-950">
      <header className="sticky top-0 z-40 border-b border-zinc-200/70 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="Web Menü ana sayfa">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[14px] bg-[#EA580C] text-white shadow-[0_10px_30px_rgba(234,88,12,0.22)]">
              <Menu className="h-5 w-5" />
            </div>
            <div className="min-w-0 leading-tight">
              <div className="truncate text-[15px] font-extrabold tracking-[-0.02em] text-zinc-950">Web Menü</div>
              <div className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400">Megriva teknolojisiyle</div>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} className="hidden rounded-full px-4 font-semibold text-zinc-700 hover:bg-zinc-100 sm:inline-flex">
              Giriş Yap
            </Button>
            <Button onClick={() => navigate("/register")} className="rounded-full bg-zinc-950 px-5 font-semibold text-white hover:bg-zinc-800">
              Hemen Başla
            </Button>
          </div>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden bg-[#FCFBF9]">
          <div aria-hidden="true" className="absolute left-[-180px] top-[-160px] h-[420px] w-[420px] rounded-full bg-orange-200/35 blur-[110px]" />
          <div aria-hidden="true" className="absolute right-[-160px] top-[80px] h-[420px] w-[420px] rounded-full bg-amber-100/70 blur-[120px]" />

          <div className="relative mx-auto max-w-6xl px-4 pb-20 pt-14 sm:px-6 sm:pt-20 lg:px-8 lg:pb-24">
            <nav aria-label="Sayfa yolu" className="mb-8 flex flex-wrap items-center gap-2 text-xs font-semibold text-zinc-500">
              <Link to="/" className="transition hover:text-[#EA580C]">Web Menü</Link>
              <span aria-hidden="true">/</span>
              <span className="text-zinc-700">{page.eyebrow}</span>
            </nav>

            <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white px-4 py-2 text-xs font-bold text-orange-700 shadow-sm">
                  <Icon className="h-4 w-4" />
                  {page.eyebrow}
                </div>
                <h1 className="mt-6 max-w-4xl text-[38px] font-extrabold leading-[1.07] tracking-[-0.045em] text-zinc-950 sm:text-5xl lg:text-[62px]">
                  {page.title}
                </h1>
                <p className="mt-6 max-w-2xl text-base font-medium leading-8 text-zinc-600 sm:text-lg">
                  {page.lead}
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Button size="lg" onClick={() => navigate("/register")} className="h-[52px] rounded-full bg-[#EA580C] px-7 text-[15px] font-bold text-white shadow-[0_14px_34px_rgba(234,88,12,0.23)] hover:bg-[#C2410C]">
                    Web Menü'nü Oluştur
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate("/")} className="h-[52px] rounded-full border-zinc-300 bg-white px-7 text-[15px] font-bold text-zinc-800 hover:bg-zinc-50">
                    Web Menü'yü İncele
                  </Button>
                </div>
              </div>

              <div className="rounded-[32px] border border-zinc-200 bg-white p-6 shadow-[0_30px_80px_rgba(24,24,27,0.08)] sm:p-8">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-[#EA580C]">
                  <BadgeCheck className="h-6 w-6" />
                </div>
                <h2 className="mt-6 text-2xl font-extrabold tracking-[-0.03em]">Web Menü ile neler yapabilirsiniz?</h2>
                <div className="mt-6 space-y-4">
                  {page.highlights.map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl bg-[#FCFBF9] px-4 py-3.5">
                      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-zinc-950 text-white"><Check className="h-3 w-3" /></span>
                      <span className="text-sm font-semibold leading-6 text-zinc-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {page.plans && (
          <section className="bg-zinc-950 py-16 text-white sm:py-20">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <div className="grid gap-5 md:grid-cols-3">
                {page.plans.map((plan) => (
                  <article key={plan.name} className="rounded-[28px] border border-white/10 bg-white/[0.05] p-6">
                    <p className="text-sm font-bold text-zinc-400">{plan.name}</p>
                    <p className="mt-4 text-4xl font-black tracking-[-0.04em]">{plan.price}</p>
                    <p className="mt-3 text-sm font-medium text-zinc-400">{plan.detail}</p>
                    <div className="mt-6 flex items-center gap-2 text-xs font-semibold text-orange-200">
                      <BadgeCheck className="h-4 w-4" />
                      Tüm mevcut Web Menü özellikleri dahil
                    </div>
                  </article>
                ))}
              </div>
              <p className="mt-5 text-center text-xs font-medium leading-6 text-zinc-500">
                Fiyat ve paket bilgileri bu sayfanın yayındaki sürümüne aittir. Güncel abonelik ekranında sunulan koşullar esas alınır.
              </p>
            </div>
          </section>
        )}

        <section className="py-18 bg-white py-20 sm:py-24">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12">
              {page.sections.map((section, index) => (
                <article key={section.title} className="grid gap-5 border-b border-zinc-200 pb-12 last:border-b-0 last:pb-0 md:grid-cols-[0.34fr_0.66fr]">
                  <div>
                    <span className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#EA580C]">0{index + 1}</span>
                    <h2 className="mt-3 text-2xl font-extrabold tracking-[-0.03em] text-zinc-950 sm:text-3xl">{section.title}</h2>
                  </div>
                  <p className="text-base font-medium leading-8 text-zinc-600">{section.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#FCFBF9] py-20 sm:py-24">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#EA580C]">Sık sorulan sorular</p>
              <h2 className="mt-3 text-3xl font-extrabold tracking-[-0.035em] text-zinc-950 sm:text-4xl">{page.eyebrow} hakkında merak edilenler</h2>
            </div>
            <div className="mt-10 grid gap-3">
              {page.faq.map(([question, answer]) => (
                <details key={question} className="group rounded-[22px] border border-zinc-200 bg-white px-5 py-4 open:shadow-sm sm:px-6">
                  <summary className="cursor-pointer list-none pr-8 text-sm font-extrabold leading-6 text-zinc-900 sm:text-base">
                    {question}
                  </summary>
                  <p className="mt-3 max-w-4xl text-sm font-medium leading-7 text-zinc-600">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex items-end justify-between gap-6">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#EA580C]">Web Menü rehberi</p>
                <h2 className="mt-3 text-2xl font-extrabold tracking-[-0.03em] sm:text-3xl">İlgili çözümleri inceleyin</h2>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {CROSS_LINKS.filter((item) => item.to !== `/${resolvedKey}`).map((item) => (
                <Link key={item.to} to={item.to} className="group rounded-[24px] border border-zinc-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-lg">
                  <p className="font-extrabold text-zinc-950">{item.label}</p>
                  <p className="mt-2 text-sm font-medium leading-6 text-zinc-500">{item.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold text-[#EA580C]">İncele <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-zinc-950 py-16 text-white sm:py-20">
          <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-300">Web Menü</p>
              <h2 className="mt-3 max-w-2xl text-3xl font-extrabold tracking-[-0.035em] sm:text-4xl">İşletmenizin dijital vitrini birkaç adımda hazır olsun.</h2>
            </div>
            <Button size="lg" onClick={() => navigate("/register")} className="h-12 shrink-0 rounded-full bg-[#EA580C] px-8 font-bold text-white hover:bg-[#C2410C]">
              Hesabını Oluştur
            </Button>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-zinc-950 text-white">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-[14px] bg-[#EA580C] text-white"><Menu className="h-5 w-5" /></div>
              <div><p className="font-extrabold">Web Menü</p><p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Megriva teknolojisiyle</p></div>
            </Link>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-zinc-400">
              <Link to="/privacy_policy" className="hover:text-white">Gizlilik Politikası</Link>
              <Link to="/terms_of_use" className="hover:text-white">Kullanım Koşulları</Link>
              <a href="https://megriva.com" target="_blank" rel="noreferrer" className="hover:text-white">Megriva</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export { PAGE_CONTENT };
